'use client';

import { useCmsComponentButton } from '@/cms/components/component-button-action';
import { cn } from '@/lib/ui/utils';
import Link from 'next/link';
import type { ReactNode } from 'react';

export function CmsEditorialButton({
  href,
  children,
  variant = 'primary',
  size = 'default',
  className,
  buttonKey,
  repeatedItem,
}: {
  href?: string | null;
  children: ReactNode;
  variant?: 'primary' | 'outline' | 'text';
  size?: 'sm' | 'default' | 'lg';
  className?: string;
  buttonKey?: string;
  repeatedItem?: { collectionKey: string; itemIndex: number };
}) {
  const runtimeButtonBinding = useCmsComponentButton(
    buttonKey ?? '',
    repeatedItem
  );
  const normalizedHref = href?.trim() ?? '';
  const resolvedClassName = cn(
    'transition-all duration-300',
    variant === 'primary'
      ? 'cms-button-primary'
      : variant === 'outline'
        ? 'cms-button-outline'
        : 'cms-button-text',
    size === 'sm' ? 'cms-button-sm' : size === 'lg' ? 'cms-button-lg' : null,
    className
  );

  if (buttonKey && runtimeButtonBinding.button) {
    if (runtimeButtonBinding.button.href) {
      if (runtimeButtonBinding.button.external) {
        return (
          <a
            className={resolvedClassName}
            href={runtimeButtonBinding.button.href}
            rel="noopener noreferrer"
            target="_blank"
          >
            {runtimeButtonBinding.button.label}
          </a>
        );
      }

      return (
        <Link
          className={resolvedClassName}
          href={runtimeButtonBinding.button.href}
        >
          {runtimeButtonBinding.button.label}
        </Link>
      );
    }

    return (
      <button
        className={resolvedClassName}
        onClick={() => runtimeButtonBinding.button?.trigger()}
        onFocus={() => runtimeButtonBinding.button?.preload?.()}
        onPointerDown={() => runtimeButtonBinding.button?.preload?.()}
        onPointerEnter={() => runtimeButtonBinding.button?.preload?.()}
        type="button"
      >
        {runtimeButtonBinding.button.label}
      </button>
    );
  }

  if (buttonKey && runtimeButtonBinding.managed) {
    return null;
  }

  if (!normalizedHref) {
    return null;
  }

  return (
    <Link href={normalizedHref} className={resolvedClassName}>
      {children}
    </Link>
  );
}
